
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { registerSchema } from "@/components/auth/register/schema";
import { useTransactionalEmail } from "@/hooks/use-transactional-email";

export type RegisterFormData = z.infer<typeof registerSchema>;

interface UseRegistrationProps {
  fromThankYou?: boolean;
  waitlistData?: boolean;
  fromClaim?: boolean;
}

export function useRegistration({ fromThankYou = false, waitlistData = false, fromClaim = false }: UseRegistrationProps = {}) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { sendEmail } = useTransactionalEmail();

  const handleSignUp = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      
      // Create the new user account
      const redirectUrl = fromClaim ? `${window.location.origin}/owner/dashboard` : `${window.location.origin}/dashboard`;
      
      const { data: userData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            user_type: fromClaim ? 'owner' : 'family'
          }
        }
      });

      if (error) {
        // Handle specific error types
        if (error.message.includes("weak password")) {
          throw new Error("Password is too weak. Please follow the password requirements.");
        }
        throw error;
      }

      // Check if email confirmation is required
      const emailConfirmationRequired = userData?.user?.identities?.length === 0 || 
                                       (userData?.user?.identities && userData?.user?.identities[0]?.identity_data?.email_verified === false);
      
      toast.success("Account created successfully!");
      
      // Send welcome email - this should happen ONLY at registration time
      try {
        const dashboardUrl = fromClaim ? `${window.location.origin}/owner/dashboard` : `${window.location.origin}/dashboard`;
        
        await sendEmail({
          type: 'welcome',
          data: {
            firstName: data.firstName || 'there',
            email: data.email,
            dashboardUrl,
            userType: fromClaim ? 'owner' : 'family',
            nursingHomeName: fromClaim ? sessionStorage.getItem('claimNursingHomeName') || 'your nursing home' : undefined
          }
        });
        
        console.log("Welcome email sent successfully");
      } catch (emailError) {
        console.error("Failed to send welcome email:", emailError);
        // Don't block the signup flow if email sending fails
      }
      
      // Flag to track when a user just completed authentication
      sessionStorage.setItem('justCompletedAuth', 'true');
      
      if (fromClaim) {
        // For claim flow, store user type and redirect appropriately
        sessionStorage.setItem('userType', 'owner');
        sessionStorage.setItem('registrationContext', 'claim');
      }
      
      // Supabase requires email confirmation before creating a session,
      // so redirect to verification page where user waits for the email link
      navigate('/auth/verify-email', { state: { email: data.email, fromClaim } });
    } catch (error: any) {
      console.error("Registration error:", error);
      
      if (error.message.includes("already been registered")) {
        toast.error("This email is already registered", {
          description: "Please use a different email or try logging in."
        });
      } else {
        toast.error("Failed to create account", {
          description: error.message || "Please try again later."
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleSignUp
  };
}
